/**
 * Signature database ported from the data-continuiti martech_scanner.py
 * (fetched from blisspointmedia/feeds-schemes @ tin-jash-patch-1, stored in
 * that branch under the filename `requirements.txt` due to a file-scrambling
 * bug — see the Digital Audit plan doc for the full identity mapping).
 *
 * Each signature is matched (case-insensitively) against combined evidence:
 * HTML, inline JS, script srcs, link/meta tags, cookies, headers, and
 * fetched first-party JS.
 *
 * A handful of source patterns contained doubled backslashes (e.g. `\\.`,
 * `\\b`) that only make sense as single-escaped regex metacharacters (no
 * signature should require a literal backslash before "lytics" or "braze").
 * These are almost certainly an artifact of the same encoding corruption
 * that scrambled this branch's filenames, not intentional patterns — they
 * are normalized to single-escaped here.
 */

export const SIGNATURES: Record<string, Record<string, RegExp[]>> = {
  'Platform / CMS': {
    WordPress: [
      /\/wp-content\//i,
      /\/wp-includes\//i,
      /wp-json/i,
      /name="generator" content="WordPress/i
    ],
    'WooCommerce (on WordPress)': [/woocommerce/i, /wc-ajax/i],
    Shopify: [/cdn\.shopify\.com/i, /Shopify\.theme/i, /myshopify\.com/i, /shopify\.shop/i],
    'Shopify Hydrogen (headless)': [
      /shopify.*hydrogen/i,
      /hydrogen-react/i,
      /storefront\.api/i,
      /cdn\.shopify\.com\/oxygen/i
    ],
    Wix: [/static\.parastorage\.com/i, /wix\.com/i, /wixstatic\.com/i],
    Squarespace: [/squarespace\.com/i, /static1\.squarespace\.com/i, /squarespace-cdn/i],
    Webflow: [/assets\.website-files\.com/i, /data-wf-site/i, /webflow\.js/i],
    Drupal: [/Drupal\.settings/i, /\/sites\/default\/files/i, /content="Drupal/i],
    Joomla: [/content="Joomla/i, /\/media\/jui\//i],
    'Magento / Adobe Commerce': [/Magento_/i, /mage\/cookies/i, /static\/version\d+\/frontend/i],
    BigCommerce: [/cdn\d*\.bigcommerce\.com/i, /bigcommerce\.com\/s-/i],
    'Salesforce Commerce Cloud (SFCC)': [
      /demandware\.static/i,
      /dwanalytics/i,
      /demandware\.store/i
    ],
    'HubSpot CMS': [/hs-sites\.com/i, /hubspotusercontent/i],
    Sitecore: [/sitecore/i, /\/-\/media\//i],
    'Adobe Experience Manager': [/\/etc\.clientlibs\//i, /\/content\/dam\//i, /cq:template/i],
    Duda: [/duda\.co/i, /dudaone/i],
    Ghost: [/content="Ghost/i, /ghost\.org/i],
    Kajabi: [/kajabi/i],
    ClickFunnels: [/clickfunnels/i],
    Framer: [/framerusercontent\.com/i, /framer\.com\/m\//i]
  },

  'Frontend Framework': {
    'Next.js': [/__NEXT_DATA__/i, /\/_next\/static/i, /next\/dist/i],
    Gatsby: [/___gatsby/i, /gatsby-/i, /page-data\.json/i],
    'Nuxt (Vue)': [/__NUXT__/i, /\/_nuxt\//i],
    'React (generic)': [/data-reactroot/i, /react-dom/i, /__REACT_DEVTOOLS/i],
    'Vue (generic)': [/data-v-app/i, /vue(?:\.runtime)?(?:\.min)?\.js/i],
    Angular: [/ng-version=/i, /angular(?:\.min)?\.js/i],
    SvelteKit: [/__sveltekit/i, /svelte-/i],
    Astro: [/astro-island/i, /name="generator" content="Astro/i],
    Remix: [/__remixContext/i, /remix\.run/i, /data-remix/i],
    Ember: [/ember-application/i]
  },

  'Headless CMS / Content API': {
    Contentful: [/cdn\.contentful\.com/i, /ctfassets\.net/i, /contentful/i],
    Sanity: [/cdn\.sanity\.io/i, /sanity\.io/i],
    Prismic: [/prismic\.io/i, /cdn\.prismic\.io/i],
    Strapi: [/strapi\.io/i, /\/strapi\//i, /strapi-/i],
    DatoCMS: [/datocms-assets\.com/i, /datocms/i],
    Storyblok: [/storyblok/i],
    Contentstack: [/contentstack/i],
    'Kontent.ai': [/kontent\.ai/i, /kenticocloud/i],
    'Builder.io': [/cdn\.builder\.io/i, /builder\.io/i],
    'GraphCMS / Hygraph': [/hygraph/i, /graphcms/i]
  },

  'Tag Management': {
    'Google Tag Manager': [/googletagmanager\.com\/gtm\.js/i, /GTM-[A-Z0-9]{4,}/i],
    'Google Tag (gtag.js)': [/googletagmanager\.com\/gtag\/js/i],
    'Tealium iQ': [/tags\.tiqcdn\.com/i, /utag\.js/i, /utag_data/i],
    'Adobe Launch / DTM': [/assets\.adobedtm\.com/i, /_satellite/i],
    Segment: [
      /cdn\.segment\.com\/analytics\.js/i,
      /analytics\.load\(/i,
      /segment\.com\/analytics/i
    ],
    Ensighten: [/nexus\.ensighten\.com/i],
    'Commanders Act (TagCommander)': [/tagcommander\.com/i, /commandersact/i],
    'Signal / BrightTag': [/btstatic\.com/i, /thebrighttag/i],
    'Matomo Tag Manager': [/matomo.*container_/i, /mtm\.js/i],
    'Piwik PRO Tag Manager': [/piwik\.pro/i, /ppms\.js/i],
    Qubit: [/qubit\.com/i, /d3c3cq33003psk/i],
    'Yandex Metrica TM': [/mc\.yandex\.ru/i]
  },

  'Server-Side Tagging Signals': {
    // Match an *assigned URL value*, never the bare token. Every GTM container
    // on the web ships these strings inside gtm.js itself — once in the
    // minified runtime's parameter-name table, and once in the literal error
    // "Server-side GTM parameters (transport_url/server_container_url) are not
    // permitted on this domain and have been ignored." The bare-token patterns
    // therefore fired on essentially every site running GTM.
    // `first_party_collection` is dropped rather than tightened: its value is a
    // boolean, which minifies to `!0`, so there is no value form that
    // distinguishes a real config from the same runtime boilerplate.
    'GTM Server-Side (transport_url / server_container_url)': [
      /transport_url['"]?\s*[:=]\s*['"]https?:\/\//i,
      /server_container_url['"]?\s*[:=]\s*['"]https?:\/\//i
    ],
    'Stape (sGTM hosting)': [/stape\.io/i, /stape-/i],
    'Addingwell (sGTM hosting)': [/addingwell/i],
    'Taggrs (sGTM hosting)': [/taggrs\.io/i],
    'Meta Conversions API Gateway': [/capig/i, /connect-gateway/i, /cag\./i],
    'Tealium EventStream / Functions hint': [/collect\.tealiumiq\.com/i, /tealium.*event.*stream/i],
    'Segment server-side hint': [/api\.segment\.io/i],
    'Snowplow (often server-side pipeline)': [
      /snowplow/i,
      /sp\.js.*collector/i,
      /\/com\.snowplowanalytics/i
    ]
  },

  Analytics: {
    // No /i on the ID pattern: Google always emits "G-" uppercase in its own
    // snippets, and case-insensitivity here would match any lowercase
    // "g-<token>" in the page (e.g. reCAPTCHA's "g-recaptcha" CSS class) as a
    // false "Google Analytics 4" detection — the same bug fixed in
    // ID_PATTERNS below, just in the stack-detection table instead of the
    // Key IDs extractor. The gtag(...) call-site pattern is unaffected
    // (case-insensitivity there has no character-class exposure).
    'Google Analytics 4': [/\bG-[A-Z0-9]{6,12}\b/, /gtag\(\s*['"]config['"]/i],
    'Universal Analytics (legacy)': [
      /\bUA-\d{4,}-\d+\b/i,
      /google-analytics\.com\/analytics\.js/i,
      /ga\('create'/i
    ],
    'Adobe Analytics': [/AppMeasurement/i, /\.sc\.omtrdc\.net/i, /2o7\.net/i, /s_code\.js/i],
    'Adobe Web SDK (Alloy)': [/alloy\.js/i, /edge\.adobedc\.net/i],
    Amplitude: [/amplitude\.com\/libs/i, /cdn\.amplitude\.com/i, /amplitude\.getInstance/i],
    Mixpanel: [/cdn\.mxpnl\.com/i, /mixpanel\.init/i],
    Heap: [/heap-\d+/i, /cdn\.heapanalytics\.com/i, /heap\.load/i],
    'Matomo / Piwik': [/matomo\.js/i, /piwik\.js/i, /_paq/i],
    Plausible: [/plausible\.io\/js/i],
    Fathom: [/usefathom\.com/i],
    Kissmetrics: [/kissmetrics/i],
    'Quantcast Measure': [/quantserve\.com/i, /quantcast/i],
    Chartbeat: [/chartbeat\.js/i, /chartbeat\.com/i],
    'Parse.ly': [/parsely/i, /pixel\.parsely\.com/i],
    'Yandex Metrica': [/mc\.yandex\.ru\/metrika/i],
    'Baidu Analytics': [/hm\.baidu\.com/i]
  },

  'Session Replay / UX Analytics': {
    Hotjar: [/static\.hotjar\.com/i, /hjSettings/i, /_hjSettings/i],
    'Microsoft Clarity': [/clarity\.ms/i, /\bclarity\(/i],
    FullStory: [/fullstory\.com/i, /window\['_fs_/i],
    Contentsquare: [/contentsquare/i, /t\.contentsquare\.net/i],
    'Quantum Metric': [/quantummetric/i, /cdn\.quantummetric\.com/i],
    Mouseflow: [/mouseflow/i],
    'Lucky Orange': [/luckyorange/i],
    LogRocket: [/logrocket/i, /cdn\.lr-ingest/i],
    Glassbox: [/glassbox/i, /cdn\.gbqofs/i],
    'Crazy Egg': [/crazyegg/i],
    PostHog: [/posthog/i]
  },

  'Advertising & Pixels': {
    'Google Ads Conversion': [/\bAW-\d{6,}\b/i, /googleadservices\.com/i, /google.*conversion/i],
    'Google Ads Remarketing': [/googleads\.g\.doubleclick\.net/i],
    'Floodlight / DV360 / CM360': [
      /\bDC-\d{4,}\b/i,
      /fls\.doubleclick\.net/i,
      /ad\.doubleclick\.net/i
    ],
    'Meta (Facebook) Pixel': [
      /connect\.facebook\.net.*fbevents/i,
      /\bfbq\(/i,
      /facebook\.com\/tr\?/i
    ],
    'TikTok Pixel': [/analytics\.tiktok\.com/i, /\bttq\./i, /tiktok.*pixel/i],
    'LinkedIn Insight Tag': [/snap\.licdn\.com/i, /_linkedin_partner_id/i],
    'Pinterest Tag': [/\bpintrk\b/i, /ct\.pinterest\.com/i, /s\.pinimg\.com\/ct/i],
    'Snapchat Pixel': [/sc-static\.net\/scevent/i, /\bsnaptr\(/i],
    'X / Twitter Pixel': [/static\.ads-twitter\.com/i, /\btwq\(/i],
    'Microsoft Ads UET (Bing)': [/bat\.bing\.com/i, /\buetq\b/i, /UET\(/i],
    'Amazon Ads': [/amazon-adsystem\.com/i, /\bamzn\(/i],
    'Reddit Pixel': [/redditstatic\.com\/ads/i, /\brdt\(/i],
    'Nextdoor Pixel': [/nextdoor\.com\/pixel/i, /\bndp\(/i],
    Taboola: [/cdn\.taboola\.com/i, /_tfa\.push/i],
    Outbrain: [/outbrain\.com/i, /\bobApi\b/i],
    'Quora Pixel': [/q\.quora\.com/i, /\bqp\(/i],
    'Spotify Ad Analytics': [/pixel\.spotify\.com/i, /podsights/i],
    'PMax/Merchant hint (Google Shopping)': [/merchant.?center/i, /google.*shopping.*feed/i],
    'Blisspoint Pixel': [/pointmediatracker\.com/i]
  },

  // The next several categories are appended dynamically in the Python source
  // (after the base SIGNATURES literal) rather than being part of the
  // original dict — some entries here (The Trade Desk, StackAdapt, etc.) were
  // moved out of "Advertising & Pixels" into "DSP / Programmatic" there too.
  'DSP / Programmatic': {
    'The Trade Desk': [/insight\.adsrvr\.org/i, /\bttd_dom_ready\b/i, /universal.?pixel/i],
    StackAdapt: [/stackadapt/i, /srv\.stackadapt\.com/i],
    'Simpli.fi': [/simpli\.fi/i],
    'Basis / Centro': [/sitescout/i],
    'Yahoo / Verizon DSP': [/ads\.yahoo\.com/i, /sp\.analytics\.yahoo\.com/i],
    AdRoll: [/adroll\.com/i, /__adroll/i],
    Criteo: [/static\.criteo\.net/i, /criteo_q/i],
    'Xandr / Microsoft Invest': [/adnxs\.com/i, /adnxs-simple\.com/i],
    Adform: [/adform\.net/i],
    'Viant / Adelphic': [/adelphic\.com/i, /ipredictive\.com/i],
    'MNTN (Connected TV)': [/d\.mountain\.com/i, /px\.mountain\.com/i],
    'Tatari (TV)': [/tatari\.tv/i],
    'Zeta Global DSP': [/zetaglobal\.(?:com|net)/i, /rezync\.com/i]
  },

  'CRM / Sales & ABM': {
    'Salesforce (Sales/Service Cloud)': [
      /webto\.salesforce\.com/i,
      /salesforceliveagent\.com/i,
      /embeddedservice\/5/i,
      /\.force\.com\//i
    ],
    'Microsoft Dynamics 365': [
      /mktdplp/i,
      /assets-\w+\.mkt\.dynamics\.com/i,
      /\w+\.dynamics\.com\/api/i
    ],
    'Zoho CRM / SalesIQ': [/salesiq\.zoho/i, /crm\.zoho/i, /zohopublic\.com/i],
    Pipedrive: [/leadbooster-chat\.pipedrive/i, /webforms\.pipedrive/i, /pipedriveassets/i],
    'Keap / Infusionsoft': [/infusionsoft\.(?:com|app)/i, /keap\.app/i],
    '6sense': [/j\.6sc\.co/i, /6sense\.com/i, /epsilon\.6sense/i],
    Demandbase: [/demandbase\.com/i, /company-target\.com/i],
    Clearbit: [/clearbitscripts\.com/i, /clearbitjs\.com/i, /tag\.clearbit/i],
    'ZoomInfo WebSights': [/ws\.zoominfo\.com/i, /zi-scripts/i],
    'Leadfeeder / Dealfront': [/lftracker/i, /leadfeeder/i, /sc\.dealfront/i],
    Albacross: [/serve\.albacross\.com/i, /albacross\.com/i],
    'Apollo.io': [/assets\.apollo\.io/i, /apollo\.io\/micro/i],
    Terminus: [/terminus\.services/i]
  },

  'Call Tracking & Intelligence': {
    Invoca: [/invoca\.net/i, /solutions\.invoca/i, /\binvoca\b/i],
    CallRail: [/callrail\.com/i, /cdn\.callrail/i],
    CallTrackingMetrics: [/calltrackingmetrics/i, /tctm\.co/i],
    Marchex: [/marchex\.io/i, /marchex\.com/i],
    WhatConverts: [/whatconverts/i],
    Ringba: [/ringba\.com/i, /\bringba\b/i],
    ResponseTap: [/responsetap/i],
    CallSource: [/callsource/i]
  },

  'Identity & First-Party Data': {
    'Google Enhanced Conversions (hashed user data)': [
      /[?&]em=tv\./i,
      /enhanced_conversions?/i,
      /[?&]ec_mode=/i
    ],
    'Meta Advanced Matching (hashed user data)': [
      /ud%5Bem%5D=/i,
      /ud\[em\]=/i,
      /ud%5Bph%5D=/i,
      /ud\[ph\]=/i
    ],
    'LiveRamp ATS / RampID': [
      /ats\.rlcdn\.com/i,
      /idsync\.rlcdn\.com/i,
      /launchpad-wrapper\.privacymanager/i
    ],
    'Unified ID 2.0': [/prod\.uidapi\.com/i, /euid\.eu/i, /uid2/i],
    'ID5 Universal ID': [/id5-sync\.com/i],
    'Retention.com (identity capture)': [/retention\.com/i, /geq\.js/i]
  },

  'Site Search & Merchandising': {
    Algolia: [/algolia\.net/i, /algolianet\.com/i, /algoliasearch/i],
    Searchspring: [/searchspring/i],
    Klevu: [/klevu\.com/i, /jsv3\.klevu/i],
    'Constructor.io': [/cnstrc\.com/i, /constructor\.io/i],
    'Bloomreach Discovery': [/brsrvr\.com/i, /bloomreach\.com/i],
    Nosto: [/nosto\.com/i, /connect\.nosto/i],
    'Fast Simon': [/fastsimon/i, /instantsearchplus/i],
    Doofinder: [/doofinder/i]
  },

  'Payments & Checkout': {
    Affirm: [/affirm\.com\/js/i, /cdn1\.affirm/i],
    Klarna: [/klarna\.com/i, /klarnaservices/i],
    Afterpay: [/afterpay\.com/i, /portal\.afterpay/i],
    Sezzle: [/sezzle/i],
    Zip: [/zip\.co\//i, /widget\.zip\.co/i],
    'Bread Pay': [/breadpayments/i, /getbread/i],
    Stripe: [/js\.stripe\.com/i, /m\.stripe\.network/i],
    PayPal: [/paypal\.com\/sdk/i, /paypalobjects\.com/i],
    Braintree: [/braintreegateway/i, /braintree-api/i],
    Bolt: [/connect\.bolt\.com/i]
  },

  'Subscription Management': {
    Recharge: [/rechargepayments/i, /rechargeapps/i],
    Ordergroove: [/ordergroove/i],
    Smartrr: [/smartrr/i],
    Skio: [/skio\.com/i],
    'Stay.ai': [/stay\.ai/i, /retextion/i],
    'Loop Subscriptions': [/loopsubscriptions/i, /loopwork\.co/i],
    'Bold Subscriptions': [/boldapps\.net/i]
  },

  'Attribution & Measurement': {
    AppsFlyer: [/appsflyer/i],
    Adjust: [/adjust\.com/i, /adj\.st/i],
    Branch: [/branch\.io/i, /app\.link/i],
    Kochava: [/kochava/i],
    Northbeam: [/northbeam\.io/i],
    'Triple Whale': [/triplewhale/i, /api\.config-security\.com\/pixel/i],
    Rockerbox: [/getrockerbox/i],
    Fospha: [/fospha/i],
    Hyros: [/hyros\.com/i, /\bhyros\b/i]
  },

  'Post-Purchase Experience': {
    Narvar: [/narvar\.com/i],
    AfterShip: [/aftership/i],
    Malomo: [/gomalomo/i, /malomo\.com/i],
    Route: [/routeapp\.io/i, /cdn\.route\.com/i],
    'Loop Returns': [/loopreturns/i],
    Returnly: [/returnly/i],
    'Happy Returns': [/happyreturns/i],
    Wonderment: [/wonderment/i]
  },

  'Scheduling & Booking': {
    Calendly: [/calendly\.com/i],
    'Acuity Scheduling': [/acuityscheduling/i],
    'Chili Piper': [/chilipiper/i],
    ServiceTitan: [/servicetitan/i],
    'Housecall Pro': [/housecallpro/i],
    Jobber: [/getjobber/i],
    OpenTable: [/opentable\.com/i],
    Resy: [/resy\.com/i],
    Tock: [/exploretock/i],
    Mindbody: [/mindbodyonline/i]
  },

  'Surveys, Quizzes & VoC': {
    Qualtrics: [/qualtrics\.com/i, /siteintercept/i],
    Medallia: [/medallia/i],
    KnoCommerce: [/knocommerce/i],
    Fairing: [/fairing\.co/i],
    Delighted: [/delighted\.com/i],
    Typeform: [/typeform\.com/i],
    Jebbit: [/jebbit/i],
    'Octane AI': [/octaneai/i]
  },

  'Affiliate / Partnership': {
    Impact: [/impactcdn\.com/i, /impactradius/i, /\bire\(/i],
    'CJ Affiliate': [/cj\.com/i, /emjcd\.com/i, /cjevent/i],
    'Rakuten Advertising': [/rakutenadvertising/i, /linksynergy/i],
    ShareASale: [/shareasale/i],
    Awin: [/awin1\.com/i, /dwin1\.com/i],
    Refersion: [/refersion/i],
    PartnerStack: [/partnerstack/i, /grsm\.io/i],
    Everflow: [/everflow/i]
  },

  'CDP / Data Platforms': {
    'Segment (CDP)': [/cdn\.segment\.com/i],
    mParticle: [/mparticle/i],
    'Tealium AudienceStream hint': [/tealiumiq/i],
    RudderStack: [/rudderstack/i, /rudderlabs/i],
    BlueConic: [/blueconic/i],
    Lytics: [/\blytics\.(com|io)/i, /jstag\.min\.js/i],
    Amperity: [/amperity/i],
    'Adobe Experience Platform': [/edge\.adobedc\.net/i, /adobedc\.demdex/i],
    'Salesforce CDP / Interaction Studio': [/evergage/i, /cdn\.evgnet\.com/i]
  },

  'Email / SMS / Marketing Automation': {
    Klaviyo: [/klaviyo/i, /static\.klaviyo\.com/i],
    Braze: [/braze\.(com|eu)/i, /appboy/i, /sdk\.iad-\d+\.braze/i],
    Iterable: [/iterable\.com/i, /js\.iterable/i],
    Mailchimp: [/mailchimp/i, /chimpstatic/i, /list-manage\.com/i],
    HubSpot: [/js\.hs-scripts\.com/i, /hs-analytics/i, /hubspot/i],
    Marketo: [/munchkin\.js/i, /munchkin\.marketo/i, /mktoresp/i],
    'Pardot (Account Engagement)': [/pi\.pardot\.com/i, /pardot/i],
    Eloqua: [/eloqua/i, /elqcfg/i],
    ActiveCampaign: [/activecampaign/i, /acdn\.io/i, /trackcmp\.net/i],
    'Attentive (SMS)': [/attentivemobile/i, /attn\.tv/i, /cdn\.attn\.tv/i],
    'Postscript (SMS)': [/postscript\.io/i, /sdk\.postscript/i],
    'Yotpo SMS (SMSBump)': [/smsbump/i],
    Omnisend: [/omnisend/i],
    'Salesforce Marketing Cloud': [/exacttarget/i, /marketingcloud/i],
    'Customer.io': [/customer\.io/i, /track\.customer\.io/i],
    Emarsys: [/emarsys/i, /scarabresearch/i],
    Listrak: [/listrak/i],
    Bluecore: [/bluecore/i],
    Cordial: [/cordial\.(io|com)/i],
    'Justuno (capture)': [/justuno/i],
    'Privy (capture)': [/privy\.com/i, /privymktg/i],
    'OptinMonster (capture)': [/optinmonster/i, /opmnstr/i],
    'Wisepops (capture)': [/wisepops/i]
  },

  'A/B Testing & Personalization': {
    Optimizely: [/cdn\.optimizely\.com/i, /optimizely/i],
    VWO: [/visualwebsiteoptimizer/i, /_vwo_code/i, /dev\.visualwebsiteoptimizer\.com/i],
    'AB Tasty': [/abtasty/i],
    'Convert.com': [/convertexperiments/i, /cdn-\d+\.convertexperiments\.com/i],
    'Adobe Target': [/(?<![\w-])at\.js/i, /tt\.omtrdc\.net/i, /adobe\.target/i, /mboxCreate/i],
    'Dynamic Yield': [/dynamicyield/i, /cdn\.dynamicyield\.com/i],
    Kameleoon: [/kameleoon/i],
    LaunchDarkly: [/launchdarkly/i],
    'Monetate / Kibo': [/monetate/i],
    Intellimize: [/intellimize/i],
    Mutiny: [/mutinyhq/i],
    'Google Optimize (sunset)': [/optimize\.js/i, /googleoptimize\.com/i]
  },

  'Consent Management': {
    OneTrust: [/onetrust/i, /cdn\.cookielaw\.org/i, /otSDKStub/i],
    Cookiebot: [/cookiebot/i, /consent\.cookiebot\.com/i],
    Usercentrics: [/usercentrics/i],
    TrustArc: [/trustarc/i, /consent\.trustarc\.com/i],
    Didomi: [/didomi/i],
    Osano: [/osano/i],
    'Quantcast Choice': [/quantcast.*choice/i, /cmp\.quantcast\.com/i],
    CookieYes: [/cookieyes/i],
    Termly: [/termly/i],
    Transcend: [/transcend\.io/i, /cdn\.transcend/i],
    Ketch: [/ketchcdn/i, /ketch\.com/i],
    Sourcepoint: [/sourcepoint/i, /sp-prod\.net/i],
    'IAB TCF present': [/__tcfapi/i],
    'US Privacy / GPP present': [/__uspapi/i, /__gpp/i],
    'Google Consent Mode': [
      /(?:google-analytics\.com|analytics\.google\.com|googletagmanager\.com|googleadservices\.com|googlesyndication\.com|doubleclick\.net|google\.com)[^\s"']*[?&]gcd=1/i,
      /(?:google-analytics\.com|analytics\.google\.com|googleadservices\.com|doubleclick\.net)[^\s"']*[?&]gcs=G1/i,
      /gtag\(\s*['"]consent['"]/i,
      /consent_mode/i,
      /default_consent/i
    ]
  },

  'Chat / Support / Engagement': {
    Intercom: [/widget\.intercom\.io/i, /intercomSettings/i],
    Drift: [/js\.driftt\.com/i, /drift\.load/i],
    Zendesk: [/zdassets\.com/i, /zendesk/i],
    LiveChat: [/livechatinc/i, /cdn\.livechat/i],
    Gorgias: [/gorgias/i],
    Tidio: [/tidio/i],
    Crisp: [/crisp\.chat/i],
    'HubSpot Chat': [/js\.usemessages\.com/i],
    Qualified: [/qualified\.com/i],
    Podium: [/podium\.(com|io)/i, /connect\.podium/i],
    Birdeye: [/birdeye/i]
  },

  'Reviews / UGC / Loyalty': {
    Yotpo: [/yotpo/i],
    Trustpilot: [/trustpilot/i],
    Bazaarvoice: [/bazaarvoice/i],
    PowerReviews: [/powerreviews/i],
    Okendo: [/okendo/i],
    Stamped: [/stamped\.io/i],
    'Smile.io (loyalty)': [/smile\.io/i, /sweettooth/i],
    LoyaltyLion: [/loyaltylion/i]
  },

  'Performance / Infrastructure': {
    Cloudflare: [/cloudflare/i, /cdn-cgi/i],
    Fastly: [/fastly/i],
    Akamai: [/akamai/i],
    Vercel: [/vercel/i, /x-vercel/i],
    Netlify: [/netlify/i],
    'AWS CloudFront': [/cloudfront\.net/i],
    'New Relic': [/newrelic/i, /nr-data\.net/i],
    'Datadog RUM': [/datadoghq/i, /dd-rum/i],
    Sentry: [/sentry(?:-cdn)?\.(?:io|com)/i, /browser\.sentry/i]
  }
};

/** Extracts a specific ID/key value out of matched evidence (e.g. a GTM container ID). */
export const ID_PATTERNS: Record<string, RegExp> = {
  // No /i flag: Google always emits these prefixes uppercase in its own
  // snippets. Case-insensitive matching let the character class also match
  // lowercase, so e.g. reCAPTCHA's "g-recaptcha" CSS class or a minified JS
  // token like "g-parseFloat" was misread as a GA4 measurement ID.
  'GTM Container IDs': /\bGTM-[A-Z0-9]{4,10}\b/,
  'GA4 Measurement IDs': /\bG-[A-Z0-9]{6,12}\b/,
  'Universal Analytics IDs (legacy)': /\bUA-\d{4,10}-\d{1,3}\b/i,
  'Google Ads Conversion IDs': /\bAW-\d{6,12}\b/i,
  'Floodlight Advertiser IDs': /\bDC-\d{4,12}\b/i,
  'Meta Pixel IDs': /fbq\(\s*['"]init['"]\s*,\s*['"](\d{8,20})['"]/i,
  'TikTok Pixel IDs': /ttq\.load\(\s*['"]([A-Z0-9]{10,30})['"]/i,
  'LinkedIn Partner IDs': /_linkedin_partner_id\s*=\s*['"]?(\d{4,10})/i,
  'Hotjar Site IDs': /hjid\s*[:=]\s*(\d{4,10})/i,
  'Tealium Profile': /tags\.tiqcdn\.com\/utag\(([\w-]+\/[\w-]+\/[\w-]+)\)\/utag\.js/i,
  'Segment Write Key': /cdn\.segment\.com\/analytics\.js\/v1\/([A-Za-z0-9]{10,40})\//i,
  'Adobe Launch Property': /assets\.adobedtm\.com\/([\w/.-]+?)\.min\.js/i
};

export interface AccessInstruction {
  access: string;
  howTo: string;
}

/** Maps a detected platform to what to request from the client during onboarding. */
export const ACCESS_MAP: Record<string, AccessInstruction> = {
  'Google Tag Manager': {
    access: 'GTM container access (Publish level)',
    howTo: 'Client adds your Google account email in GTM > Admin > User Management'
  },
  'Google Tag (gtag.js)': {
    access: 'Access to whichever Google product the gtag routes to (GA4 / Ads)',
    howTo: 'See GA4 and Google Ads rows; confirm destination IDs from this report'
  },
  'Tealium iQ': {
    access: 'Tealium iQ account access (Publish rights on the profile)',
    howTo:
      'Client invites you in Tealium iQ > User Management; note account/profile from this report'
  },
  'Adobe Launch / DTM': {
    access: 'Adobe Experience Cloud org access (Launch property, Develop/Publish rights)',
    howTo: 'Client admin adds you in Adobe Admin Console; may require enterprise ID'
  },
  Segment: {
    access: 'Segment workspace access',
    howTo: 'Client invites you in Segment > Settings > Workspace > Team'
  },
  Ensighten: {
    access: 'Ensighten Manage account access',
    howTo: 'Client admin invites via Ensighten user management'
  },
  'Google Analytics 4': {
    access: 'GA4 property access (Editor for implementation, Analyst minimum for reporting)',
    howTo:
      'Admin > Property Access Management; request account-level if managing multiple properties'
  },
  'Universal Analytics (legacy)': {
    access: 'UA property access (historical data only — UA stopped processing in 2023/24)',
    howTo: 'Admin > Property Access Management; useful for YoY baselines'
  },
  'Adobe Analytics': {
    access: 'Adobe Analytics access (workspace + report suite)',
    howTo: 'Adobe Admin Console; request Analysis Workspace access at minimum'
  },
  'Adobe Web SDK (Alloy)': {
    access: 'Adobe Experience Platform datastream/schema access',
    howTo: 'Adobe Admin Console — AEP permissions'
  },
  Amplitude: {
    access: 'Amplitude project access',
    howTo: 'Client invites via Amplitude Settings > Members'
  },
  Mixpanel: {
    access: 'Mixpanel project access',
    howTo: 'Client invites via Mixpanel project settings'
  },
  Heap: { access: 'Heap workspace access', howTo: 'Client invites via Heap account settings' },
  'Google Ads Conversion': {
    access: 'Google Ads account link (via your manager/MCC account)',
    howTo: 'Send link request with client Customer ID; client approves in Google Ads'
  },
  'Google Ads Remarketing': {
    access: 'Google Ads account access (audiences)',
    howTo: 'Covered by the Google Ads MCC link above'
  },
  'Floodlight / DV360 / CM360': {
    access: 'Google Marketing Platform access (CM360 advertiser and/or DV360 partner)',
    howTo: 'Client GMP admin grants advertiser access; confirm Floodlight config ownership'
  },
  'Meta (Facebook) Pixel': {
    access: 'Meta Business Manager partner access (ad account + pixel/dataset + page)',
    howTo: 'Client assigns your Business Portfolio as Partner with appropriate asset permissions'
  },
  'TikTok Pixel': {
    access: 'TikTok Business Center partner access (advertiser account + pixel)',
    howTo: 'Client adds your Business Center as partner'
  },
  'LinkedIn Insight Tag': {
    access: 'LinkedIn Campaign Manager account access (+ Insight Tag ownership check)',
    howTo: 'Client grants Account Manager role in Campaign Manager'
  },
  'Pinterest Tag': {
    access: 'Pinterest Business Hub access (ad account + tag)',
    howTo: 'Client invites via Business Access'
  },
  'Snapchat Pixel': {
    access: 'Snapchat Business Manager access (ad account + pixel)',
    howTo: 'Client adds you as member/partner in Business Manager'
  },
  'X / Twitter Pixel': {
    access: 'X Ads account access',
    howTo: 'Client grants access in ads.x.com account settings'
  },
  'Microsoft Ads UET (Bing)': {
    access: 'Microsoft Advertising account access (via agency/manager account)',
    howTo: 'Send link request with client account number; client approves'
  },
  'Amazon Ads': {
    access: 'Amazon Ads console access (advertiser account)',
    howTo: 'Client invites via Amazon Ads user management or grants via Amazon DSP seat'
  },
  'Reddit Pixel': {
    access: 'Reddit Ads account access',
    howTo: 'Client invites via Reddit Ads > Members'
  },
  Criteo: { access: 'Criteo account access', howTo: 'Client requests via Criteo account team' },
  Taboola: { access: 'Taboola account access', howTo: 'Client invites via Taboola Backstage' },
  Outbrain: {
    access: 'Outbrain Amplify account access',
    howTo: 'Client invites via Amplify settings'
  },
  'The Trade Desk': {
    // ACCESS_MAP.update() in the Python source overwrites the initial entry with this one.
    access: 'Trade Desk seat access (read or trader level)',
    howTo: "Client's TTD account manager adds users under Partner/Advertiser settings"
  },
  StackAdapt: {
    access: 'StackAdapt account access',
    howTo: 'Client invites via StackAdapt user management'
  },
  AdRoll: { access: 'AdRoll account access', howTo: 'Client invites via AdRoll settings' },
  Impact: {
    access: 'Impact.com account access (program-level)',
    howTo: 'Client invites via impact.com user management'
  },
  'CJ Affiliate': {
    access: 'CJ advertiser account access',
    howTo: 'Client adds user in CJ Account Manager'
  },
  'Rakuten Advertising': {
    access: 'Rakuten advertiser account access',
    howTo: 'Via Rakuten account team'
  },
  ShareASale: {
    access: 'ShareASale merchant account access',
    howTo: 'Client shares agency login or adds user'
  },
  Awin: {
    access: 'Awin advertiser account access',
    howTo: 'Client invites via Awin user management'
  },
  WordPress: {
    access: 'WordPress admin (or Editor + plugin management) login',
    howTo: 'Needed for tag/plugin changes; ask about staging environment and deploy process'
  },
  'WooCommerce (on WordPress)': {
    access: 'WooCommerce settings access (within WP admin)',
    howTo: 'Same WP admin; confirm order/conversion data access'
  },
  Shopify: {
    access: 'Shopify collaborator account (staff access: themes, settings, apps, pixels)',
    howTo: "Request via Shopify Partners collaborator request with client's store URL"
  },
  Wix: {
    access: 'Wix site contributor access (Admin or Website Manager role)',
    howTo: 'Client invites via Wix Roles & Permissions'
  },
  Squarespace: {
    access: 'Squarespace contributor access (Website Editor + Settings)',
    howTo: 'Client invites via Permissions'
  },
  Webflow: {
    access: 'Webflow workspace/site access (can publish)',
    howTo: 'Client invites as workspace member or site guest'
  },
  'Magento / Adobe Commerce': {
    access: 'Magento admin access (scoped role) or dev team contact',
    howTo: 'Tagging changes usually go through client dev/release process'
  },
  BigCommerce: {
    access: 'BigCommerce store user access (script manager)',
    howTo: 'Client invites via Account Settings > Users'
  },
  'Salesforce Commerce Cloud (SFCC)': {
    access: 'Business Manager access (scoped) or dev team contact',
    howTo: 'Tag changes typically require client dev sprint'
  },
  'HubSpot CMS': {
    access: 'HubSpot account access (Marketing + CMS scopes)',
    howTo: 'Client invites via Settings > Users & Teams'
  },
  Drupal: {
    access: 'Drupal admin access or dev team contact',
    howTo: 'Tag changes typically via dev process'
  },
  Sitecore: {
    access: 'Dev team contact (Sitecore changes go through client engineering)',
    howTo: 'Plan lead time for tagging work'
  },
  'Adobe Experience Manager': {
    access: 'Dev team contact (AEM changes go through client engineering)',
    howTo: 'Plan lead time for tagging work'
  },
  Klaviyo: {
    access: 'Klaviyo account access',
    howTo: 'Client invites via Settings > Users; useful for audience sync + revenue attribution'
  },
  Braze: { access: 'Braze workspace access', howTo: 'Client invites via Braze user management' },
  Iterable: { access: 'Iterable project access', howTo: 'Client invites via Iterable settings' },
  Mailchimp: { access: 'Mailchimp account access', howTo: 'Client invites via Account > Users' },
  HubSpot: {
    access: 'HubSpot account access (Marketing Hub)',
    howTo: 'Client invites via Settings > Users & Teams'
  },
  Marketo: {
    access: 'Marketo access (scoped role)',
    howTo: 'Client admin invites via Admin > Users & Roles'
  },
  'Pardot (Account Engagement)': {
    access: 'Account Engagement access (via Salesforce SSO)',
    howTo: 'Client Salesforce admin provisions'
  },
  'Attentive (SMS)': {
    access: 'Attentive account access',
    howTo: 'Client invites via Attentive settings'
  },
  'Salesforce Marketing Cloud': {
    access: 'SFMC business unit access',
    howTo: 'Client admin provisions user'
  },
  'Segment (CDP)': {
    access: 'Segment workspace access',
    howTo: 'Client invites via workspace team settings'
  },
  mParticle: {
    access: 'mParticle workspace access',
    howTo: 'Client invites via mParticle user management'
  },
  RudderStack: {
    access: 'RudderStack workspace access',
    howTo: 'Client invites via workspace settings'
  },
  Optimizely: { access: 'Optimizely project access', howTo: 'Client invites via account settings' },
  VWO: { access: 'VWO account access', howTo: 'Client invites via VWO user management' },
  'AB Tasty': { access: 'AB Tasty account access', howTo: 'Client invites via user management' },
  'Adobe Target': {
    access: 'Adobe Target access (Adobe Admin Console)',
    howTo: 'Client Adobe admin provisions'
  },
  'Dynamic Yield': {
    access: 'Dynamic Yield account access',
    howTo: 'Client invites via DY user management'
  },
  OneTrust: {
    access: 'OneTrust access (or at minimum, category mapping documentation)',
    howTo: 'Needed to register new tags in consent categories before launch'
  },
  Cookiebot: {
    access: 'Cookiebot account access (or consent category documentation)',
    howTo: 'New tags must be classified for consent'
  },
  Usercentrics: {
    access: 'Usercentrics admin access (or consent config documentation)',
    howTo: 'New tags must be added to the CMP service list'
  },
  TrustArc: {
    access: 'TrustArc consent manager access (or documentation)',
    howTo: 'Coordinate tag categorization'
  },
  Didomi: {
    access: 'Didomi console access (or documentation)',
    howTo: 'Coordinate tag categorization'
  },
  'GTM Server-Side (transport_url / server_container_url)': {
    access: 'Server GTM container access + hosting access (GCP project or sGTM host account)',
    howTo: 'Ask who owns the server container and where it is hosted (GCP, Stape, etc.)'
  },
  'GTM loaded via custom domain (likely sGTM first-party serving)': {
    access: 'Server GTM container + DNS/hosting context for the custom tagging domain',
    howTo: 'Confirm ownership — sometimes set up by a previous agency'
  },
  'Stape (sGTM hosting)': {
    access: 'Stape account access (sGTM hosting)',
    howTo: 'Client invites via Stape workspace'
  },
  'Snowplow (often server-side pipeline)': {
    access: 'Snowplow pipeline access / data warehouse access',
    howTo: 'Coordinate with client data engineering'
  },
  'Salesforce (Sales/Service Cloud)': {
    access: 'Salesforce read access or scheduled report exports',
    howTo: 'Client admin adds a user in Setup > Users, or shares CRM pipeline reports'
  },
  'Microsoft Dynamics 365': {
    access: 'Dynamics 365 report access (or pipeline exports)',
    howTo: 'Client admin grants a security role, or shares recurring exports'
  },
  '6sense': {
    access: '6sense platform access (read)',
    howTo: 'Client admin invites users under Settings > User Management'
  },
  Demandbase: {
    access: 'Demandbase platform access (read)',
    howTo: 'Client admin invites users in Settings'
  },
  Invoca: {
    access: 'Invoca platform access (read) or call report exports',
    howTo: 'Client admin invites users under Settings > Manage Users'
  },
  CallRail: {
    access: 'CallRail account access (Reporting level)',
    howTo: 'Client admin adds users under Settings > Users'
  }
};

/**
 * Display order for detection categories in reports/briefs. Mirrors the
 * final order produced by martech_scanner.py's CATEGORY_ORDER.insert() calls
 * (the base list plus categories appended after the SIGNATURES literal).
 */
export const CATEGORY_ORDER = [
  'Platform / CMS',
  'Frontend Framework',
  'Headless CMS / Content API',
  'Tag Management',
  'Server-Side Tagging Signals',
  'Analytics',
  'Session Replay / UX Analytics',
  'Advertising & Pixels',
  'DSP / Programmatic',
  'Affiliate / Partnership',
  'CDP / Data Platforms',
  'CRM / Sales & ABM',
  'Call Tracking & Intelligence',
  'Email / SMS / Marketing Automation',
  'A/B Testing & Personalization',
  'Consent Management',
  'Chat / Support / Engagement',
  'Identity & First-Party Data',
  'Site Search & Merchandising',
  'Payments & Checkout',
  'Subscription Management',
  'Attribution & Measurement',
  'Post-Purchase Experience',
  'Scheduling & Booking',
  'Surveys, Quizzes & VoC',
  'Reviews / UGC / Loyalty',
  'Performance / Infrastructure'
];
